import React, { ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  tabName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class AdminErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Admin Panel Error caught by ErrorBoundary:", error, errorInfo);
  }

  public override componentDidUpdate(prevProps: Props) {
    if (prevProps.tabName !== this.props.tabName && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  public handleReset = () => {
    const isChunkError =
      this.state.error?.message?.includes("Failed to fetch dynamically imported module") ||
      this.state.error?.message?.includes("Importing a module script failed") ||
      this.state.error?.message?.includes("Loading chunk");

    if (isChunkError) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  public override render() {
    if (this.state.hasError) {
      const isChunkError =
        this.state.error?.message?.includes("Failed to fetch dynamically imported module") ||
        this.state.error?.message?.includes("Importing a module script failed") ||
        this.state.error?.message?.includes("Loading chunk");

      return (
        <div className="bg-white border border-rose-200 rounded-2xl p-8 text-center space-y-4 shadow-sm max-w-2xl mx-auto my-6">
          <div className="w-12 h-12 bg-rose-50 text-rose-600 rounded-full flex items-center justify-center mx-auto border border-rose-100">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-base font-extrabold text-gray-900">
              Unable to load {this.props.tabName || "Admin Section"}
            </h3>
            <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
              {isChunkError
                ? "A new version of RentLow was recently deployed. Click below to reload and fetch the latest module updates."
                : this.state.error?.message || "An unexpected rendering error occurred. The system trapped this safely to keep the rest of your dashboard functioning."}
            </p>
          </div>
          <div className="pt-2 flex justify-center gap-3">
            <button
              onClick={this.handleReset}
              className="inline-flex items-center gap-1.5 bg-gray-900 hover:bg-black text-white text-xs font-bold px-4 py-2 rounded-xl transition-all shadow-sm cursor-pointer"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {isChunkError ? "Update & Reload" : "Try Again"}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold px-4 py-2 rounded-xl transition-all cursor-pointer"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
